<template>
    <ToolbarAbstract class="toolbar-image-context">

        <!-- TOOLS -->
        <v-btn-toggle class="toggle" elevation="1" v-model="tool" mandatory>

            <!-- Sketch Tool -->
            <v-btn 
                v-if="isSketch"
                elevation="1" 
                class="toggle-button" 
                :class="{'active': isToolSketch}"
                @click="handleToolSketch">
                <v-tooltip
                    activator="parent"
                    location="right">
                    select sketch tool
                </v-tooltip>
                <v-icon>fa-solid fa-pencil</v-icon>
            </v-btn>

            <!-- Highlight Tool -->
            <v-btn 
                v-else
                elevation="1" 
                class="toggle-button" 
                :class="{'active': isToolMarker}"
                @click="handleToolMarker">
                <v-tooltip
                    activator="parent"
                    location="right">
                    select highlight tool
                </v-tooltip>
                <v-icon>fa-solid fa-highlighter</v-icon>
            </v-btn>

            <!-- Erase Tool -->
            <v-btn 
                elevation="1" 
                class="toggle-button" 
                :class="{'active': isToolErase}"
                @click="handleToolErase">
                <v-tooltip
                    activator="parent"
                    location="right">
                    select erase tool
                </v-tooltip>
                <v-icon>fa-solid fa-eraser</v-icon>
            </v-btn>

            <!-- Crop Tool -->
            <v-btn 
                elevation="1" 
                class="toggle-button" 
                :class="{'active': isToolCrop}" 
                @click="handleToolCrop">
                <v-tooltip
                    activator="parent"
                    location="right">
                    select crop tool
                </v-tooltip>
                <v-icon>fa-solid fa-crop-simple</v-icon>
            </v-btn>
        </v-btn-toggle> 

        <Separator />

        <!-- FUNCTIONS -->

        <!-- Background Remove -->
        <v-btn 
            v-if="!isSketch"
            color="darkbase"
            elevation="1" 
            @click="handleBackground">
            <v-tooltip
                activator="parent"
                location="right">
                remove background
            </v-tooltip>         
            <v-icon>fa-regular fa-image</v-icon>
        </v-btn>

        <!-- Rotate -->
        <v-btn 
            color="darkbase"
            elevation="1" 
            @click="handleRotate">
            <v-tooltip
                activator="parent"
                location="right">
                rotate image
            </v-tooltip>  
            <v-icon>fa-solid fa-arrows-spin</v-icon>
        </v-btn>

        <Separator />

        <!-- HISTORY -->

        <div class="extended">

            <!-- Undo -->
            <v-btn 
                color="darkbase"
                elevation="1" 
                @click="handleUndo">
                <v-tooltip
                    activator="parent"
                    location="right">
                    undo last step
                </v-tooltip>
                <v-icon>fa-solid fa-rotate-left</v-icon>
            </v-btn>

            <!-- Redo -->
            <v-btn 
                color="darkbase"
                elevation="1" 
                @click="handleRedo">
                <v-tooltip
                    activator="parent"
                    location="right">
                    redo last step
                </v-tooltip>
                <v-icon>fa-solid fa-rotate-right</v-icon>
            </v-btn>

            <!-- Reset -->
            <v-btn
                color="darkbase"
                elevation="1"
                @click="handleReset">
                <v-tooltip
                    activator="parent"
                    location="right">
                    revert all changes
                </v-tooltip>
                <v-icon>fa-solid fa-broom</v-icon>
            </v-btn>

            <Separator />

            <!-- Download -->
            <v-btn 
                color="darkbase"
                elevation="1"
                @click="handleDownload">
                <v-tooltip
                    activator="parent"
                    location="right">
                    download image
                </v-tooltip>
                <v-icon>fa-solid fa-download</v-icon>
            </v-btn>

        </div>
        <div class="collapsed">
            <v-speed-dial
                location="bottom center"
                transition="slide-y-transition" 
                v-model="opened">

                <template v-slot:activator="{ props }">
                    <v-btn 
                        v-bind="props"
                        color="darkbase"
                        elevation="1">
                        <v-tooltip
                            activator="parent"
                            location="right">
                            more options
                        </v-tooltip>
                        <v-icon>fa-solid fa-ellipsis-vertical</v-icon>
                    </v-btn>
                </template>

                <!-- Undo -->
                <v-btn 
                    key="1" 
                    color="darkbase"
                    elevation="1" 
                    style="height:40px;"
                    @click="handleUndo">
                    <v-tooltip
                        activator="parent"
                        location="right">
                        undo last step
                    </v-tooltip>
                    <v-icon>fa-solid fa-rotate-left</v-icon>
                </v-btn>

                <!-- Redo -->
                <v-btn 
                    key="2"
                    color="darkbase"
                    elevation="1" 
                    style="height:40px;"
                    @click="handleRedo">
                    <v-tooltip
                        activator="parent"
                        location="right">
                        redo last step
                    </v-tooltip>
                    <v-icon>fa-solid fa-rotate-right</v-icon>
                </v-btn>

                <!-- Reset -->
                <v-btn
                    key="3"
                    color="darkbase"
                    elevation="1" 
                    style="height:40px;"
                    @click="handleReset">
                    <v-tooltip
                        activator="parent"
                        location="right">
                        revert all changes
                    </v-tooltip>
                    <v-icon>fa-solid fa-broom</v-icon>
                </v-btn>

                <!-- Download -->
                <v-btn
                    key="4"
                    color="darkbase"
                    elevation="1"
                    style="height:40px;"
                    @click="handleDownload">
                    <v-tooltip
                        activator="parent"
                        location="right">
                        download image
                    </v-tooltip>
                    <v-icon>fa-solid fa-download</v-icon>
                </v-btn>

            </v-speed-dial>
        </div>

    </ToolbarAbstract>
</template>

<script>
import DIALOGS from '@generio-ai/library-legacy/constants/dialogs'
import { mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import ToolbarAbstract from '@/components/common/toolbar/ToolbarAbstract.vue'
import Separator from '@/components/common/toolbar/Separator.vue'

export default {
    name: 'ToolbarImageContext',
    components: {
        ToolbarAbstract,
        Separator
    },
    props: {
        isSketch: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            tool: null,
            opened: false
        }
    },
    computed: {
        keyToolSketch() {
            return 'sketch'
        },
        keyToolMarker() {
            return 'marker'
        },
        keyToolErase() {
            return 'erase'
        },
        keyToolCrop() {
            return 'crop'
        },
        isToolSketch() {
            return this.tool === this.keyToolSketch
        },
        isToolMarker() {
            return this.tool === this.keyToolMarker
        },
        isToolErase() {
            return this.tool === this.keyToolErase
        },
        isToolCrop() {
            return this.tool === this.keyToolCrop
        }
    },
    emits: [
        'update:tool',
        'rotate',
        'background',
        'undo',
        'redo',
        'reset',
        'download'
    ],
    methods: {
        ...mapActions('dialogs', [
            'open'
        ]),
        handleToolSketch() {
            this.tool = this.keyToolSketch
            this.$emit('update:tool', this.keyToolSketch)
        },
        handleToolMarker() {
            this.tool = this.keyToolMarker
            this.$emit('update:tool', this.keyToolMarker)
        },
        handleToolErase() {
            this.tool = this.keyToolErase
            this.$emit('update:tool', this.keyToolErase)
        },
        handleToolCrop() {
            this.tool = this.keyToolCrop
            this.$emit('update:tool', this.keyToolCrop)
        },
        handleRotate() {
            this.$emit('rotate')
        },
        handleBackground() {
            this.$emit('background')
        },
        handleUndo() {
            this.$emit('undo')
        },
        handleRedo() {
            this.$emit('redo')
        },
        handleReset() {
            this.open({
                component: DIALOGS.CHOICE,
                payload: {
                    title: 'Revert&nbsp;All&nbsp;Changes',
                    message: 'Are you sure you want to revert all changes?',
                    firstLabel: 'Cancel',
                    firstColor: 'surface',
                    onFirst: () => {},
                    secondLabel: 'Continue',
                    secondColor: 'color1',
                    onSecond: () => this.$emit('reset'),
                }
            })
        },
        handleDownload() {
            this.$emit('download')
        }
    },
    mounted() {
        if (this.isSketch) this.handleToolSketch()
        else this.handleToolMarker()
    }
}
</script>

<style scoped>
.toolbar-image-context .toggle {
    flex-shrink: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: unset;
    min-height: 150px;
    border-radius: 8px;
}

.toolbar-image-context .toggle .v-btn:not(:first-child) {
    border-top: 1px solid rgb(var(--v-theme-lightbase));
}

.toolbar-image-context .toggle .v-btn {
    border-radius: 0px !important;
    background: rgb(var(--v-theme-darkbase));
}

.toolbar-image-context .toggle .v-btn.active {
    color: rgb(var(--v-theme-white)) !important;
    background: linear-gradient(0deg, rgb(var(--v-theme-color2)), rgb(var(--v-theme-color1)));
}

.toolbar-image-context .extended {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
}

.toolbar-image-context .collapsed {
    display: none;
}

@media (max-width: 600px) {
    .toolbar-image-context .toggle {
        flex-direction: row;
        width: unset;
        min-height: unset;
    }

    .toolbar-image-context .extended {
        flex-direction: row;
    }
}

@media (max-width: 500px) {
    .toolbar-image-context .extended {
        display: none;
    }

    .toolbar-image-context .collapsed {
        display: block;
    }
}
</style>